import { useEffect, useRef, useState } from 'react';

const DEFAULT_DEBOUNCE_MS = 200;

/**
 * Debounce a value: only updates the returned value once `debounceMs` has elapsed since the last
 * change to `value`. Unlike `useThrottledValue` (which guarantees periodic updates while a value
 * keeps changing), this waits for changes to settle before reflecting the latest one.
 */
export const useDebouncedValue = <T>(value: T, debounceMs = DEFAULT_DEBOUNCE_MS): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, debounceMs);

    return () => {
      if (timeoutRef.current != null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [value, debounceMs]);

  return debouncedValue;
};
